"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const ts_validators_1 = require("ts-validators");
const short_uuid_1 = __importDefault(require("short-uuid"));
const flatControl_1 = __importDefault(require("../../functions/flatters/flatControl"));
const unwrapReadonlyObject_1 = __importDefault(require("../../shared/unwrapReadonlyObject"));
const isMultipleListModification_1 = __importDefault(require("../../typeAssertions/isMultipleListModification"));
const valueIsNullOrUndef_1 = __importDefault(require("../../typeAssertions/valueIsNullOrUndef"));
const applyModifications_1 = __importDefault(require("./applyModifications"));
const AppStateConstants_1 = require("../../types/AppStateConstants");
const maxNumberOfItemsPerUpdate = 150;
const reasignIdsToControl = (control) => {
    const newControl = (0, unwrapReadonlyObject_1.default)(control);
    newControl.id = short_uuid_1.default.generate();
    newControl.controls = (control.controls || [])
        .map((childControl) => {
        if ((0, ts_validators_1.valueIsString)(childControl)) {
            return control;
        }
        return reasignIdsToControl(childControl);
    });
    return newControl;
};
const buildProgresiveUpdater = ({ controlCallerId, processor, itemContexts, lastFunctionsOutputs, listInstructions: { listControlId, modifications = {}, listStyles, listOptions, }, scrollInverted, extraFunctionDefinitions, numColumns, isHorizontal, onDone, isSwiper, store, }) => {
    let shouldContinue = true;
    let isFirstRender = true;
    let currentIndexToStart = 0;
    const listControlOriginalInfo = processor.root.getControlInfoById(listControlId);
    if (!listControlOriginalInfo) {
        return { cancel: () => { } };
    }
    const listControlOriginal = listControlOriginalInfo.control;
    if (!listControlOriginal) {
        return { cancel: () => { } };
    }
    const makeIteration = async () => {
        var _a, _b, _c;
        if (!shouldContinue) {
            return;
        }
        const asyncControlUpdates = [];
        let { listItemControlTemplate } = listControlOriginal;
        const isFirstTimeGenerating = !listItemControlTemplate;
        const originalFirstChildRaw = (_a = listControlOriginal.controls) === null || _a === void 0 ? void 0 : _a[0];
        const originalFirstChild = (0, ts_validators_1.valueIsString)(originalFirstChildRaw)
            ? undefined : originalFirstChildRaw;
        const filterNonLegacyControls = (child) => !(0, ts_validators_1.valueIsString)(child);
        const originalFirstChildControls = (_b = originalFirstChild === null || originalFirstChild === void 0 ? void 0 : originalFirstChild.controls) === null || _b === void 0 ? void 0 : _b.filter(filterNonLegacyControls);
        if (isFirstTimeGenerating) {
            // El template de la lista aun no se a generado
            if (isSwiper) {
                listItemControlTemplate = originalFirstChild;
            }
            else if (((_c = originalFirstChild === null || originalFirstChild === void 0 ? void 0 : originalFirstChild.controls) === null || _c === void 0 ? void 0 : _c.length) === 1) {
                const firstGrandChild = originalFirstChild.controls[0];
                listItemControlTemplate = (0, ts_validators_1.valueIsString)(firstGrandChild)
                    ? undefined : firstGrandChild;
            }
            else {
                // Multiple templates
                listItemControlTemplate = originalFirstChildControls;
            }
        }
        if (!listItemControlTemplate) {
            throw new Error('The listItemControl was not found');
        }
        const filterItemsInRange = (_, index) => index >= currentIndexToStart // eslint-disable-line
            && index < (currentIndexToStart + maxNumberOfItemsPerUpdate);
        let listControlsInfo = {};
        const filteredListControls = itemContexts.filter(filterItemsInRange);
        let listControls = [];
        let isMultipleList = false;
        for (let listControlIndex = 0; listControlIndex < filteredListControls.length; listControlIndex += 1) {
            let listItemControlTemplateCopy = listItemControlTemplate;
            let listModifications = modifications;
            const itemContext = filteredListControls[listControlIndex];
            const ListOSelectorProp = listOptions === null || listOptions === void 0 ? void 0 : listOptions.selectorProp;
            if (!(0, valueIsNullOrUndef_1.default)(ListOSelectorProp)) {
                const selectorProp = itemContext.data[ListOSelectorProp];
                isMultipleList = true;
                Object.entries(modifications).forEach(([controlId, modification]) => {
                    if (modification
                        && (0, isMultipleListModification_1.default)(modification)
                        && selectorProp === modification.selectorValue) {
                        if (!Array.isArray(listItemControlTemplate)) {
                            throw new Error('When using a selector, the list must have more than one list');
                        }
                        const listControl = isFirstTimeGenerating
                            ? originalFirstChildControls
                            : listItemControlTemplate;
                        listItemControlTemplateCopy = listControl === null || listControl === void 0 ? void 0 : listControl.find((control) => control.id === controlId);
                        listModifications = modification.itemModifications;
                    }
                });
            }
            if ((0, valueIsNullOrUndef_1.default)(listItemControlTemplateCopy)
                || Array.isArray(listItemControlTemplateCopy)) {
                throw new Error('List item should be a control');
            }
            let itemControl;
            if (isFirstTimeGenerating || isMultipleList) {
                itemControl = reasignIdsToControl(listItemControlTemplateCopy);
                itemControl.id = itemContext.id;
            }
            else {
                const oldItemControl = (listControlOriginal.controls || [])
                    .filter(filterNonLegacyControls)
                    .find((originalControlItem) => originalControlItem.id === itemContext.id);
                if (oldItemControl) {
                    itemControl = oldItemControl;
                }
                else {
                    itemControl = reasignIdsToControl(listItemControlTemplateCopy);
                    itemControl.id = itemContext.id;
                }
            }
            const controlContext = {
                lastFunctionsOutputs,
                itemContext,
                processor,
                extraFunctionDefinitions,
            };
            processor.root.setControlRunContext(itemControl.id, controlContext);
            itemControl = await (0, applyModifications_1.default)({
                asyncControlUpdates,
                modifications: listModifications,
                listItemControlTemplate: listItemControlTemplateCopy,
                itemControl,
                controlContext,
                processor,
                controlCallerId,
                lastFunctionsOutputs,
                itemContext,
            });
            if (itemControl.isDynamicLoadingEnabled) {
                itemControl = Object.assign(Object.assign({}, itemControl), { isDynamicLoadingEnabled: false });
            }
            listControlsInfo = (0, flatControl_1.default)({
                control: itemControl,
                parentControlId: listControlId,
                initialObject: listControlsInfo,
                path: [
                    ...(listControlOriginalInfo.path || []),
                    listControlIndex + currentIndexToStart,
                ],
                context: listControlOriginalInfo.context,
            });
            listControls = [
                ...listControls,
                itemControl,
            ];
        }
        if (isFirstRender) {
            if ((0, valueIsNullOrUndef_1.default)(listItemControlTemplate)) {
                throw new Error('List item should be a control');
            }
            isFirstRender = false;
            if (isSwiper) {
                store.dispatch({
                    type: AppStateConstants_1.AppStateConstants.BO_UPDATE_SWIPER_CONTENT,
                    payload: {
                        controlId: listControlId,
                        listItemControlTemplate,
                        controls: listControls,
                    },
                });
                store.dispatch({
                    type: AppStateConstants_1.AppStateConstants.BO_UPDATE_CONTROL_BY_ID,
                    payload: {
                        control: listControlOriginal,
                        dataToUpdate: {
                            isHidden: false,
                        },
                    },
                });
            }
            else {
                store.dispatch({
                    type: AppStateConstants_1.AppStateConstants.BO_UPDATE_CONTROL_TO_LIST,
                    payload: {
                        controlId: listControlId,
                        shouldAddTabsToList: !!(listOptions === null || listOptions === void 0 ? void 0 : listOptions.categorytabsEnabled),
                        inverted: !!scrollInverted,
                        categoryField: listOptions === null || listOptions === void 0 ? void 0 : listOptions.categoryField,
                        listItemControlTemplate,
                        controls: listControls,
                        listStyles,
                        filterEnabled: !!(listOptions === null || listOptions === void 0 ? void 0 : listOptions.filterListEnabled),
                        filterProperty: (listOptions === null || listOptions === void 0 ? void 0 : listOptions.filterProperty) || '',
                        filterPlaceholder: listOptions === null || listOptions === void 0 ? void 0 : listOptions.filterPlaceholder,
                        listControlsInfo,
                        numColumns,
                        isHorizontal: !!isHorizontal,
                    },
                });
            }
        }
        else {
            store.dispatch({
                type: AppStateConstants_1.AppStateConstants.BO_ADD_ITEMS_TO_FLAT_LIST,
                payload: {
                    controlId: listControlId,
                    controls: listControls,
                    listControlsInfo,
                },
            });
        }
        currentIndexToStart += maxNumberOfItemsPerUpdate;
        if (currentIndexToStart < itemContexts.length) {
            makeIteration();
        }
        else if (onDone) {
            onDone();
        }
        if (asyncControlUpdates.length === 0)
            return;
        setTimeout(() => {
            asyncControlUpdates.forEach(({ promiseValueComputer, controlId, propPath, }) => {
                promiseValueComputer().then((actualValue) => {
                    store.dispatch({
                        type: AppStateConstants_1.AppStateConstants.BO_UPDATE_CONTROL_PROP_BY_PATH_IF_EXIST,
                        payload: {
                            controlId,
                            propPath,
                            value: actualValue,
                        },
                    });
                });
            });
        }, 20);
    };
    return {
        cancel: () => {
            shouldContinue = false;
        },
        makeIteration,
    };
};
exports.default = buildProgresiveUpdater;
